import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";
import React, { useEffect, useState } from "react";
import { useLocation } from 'react-router-dom';
import { AlertType, PageType, TenantID, } from "src/common/enums";
import { BreadCrumbs } from "src/components/breadcrumb";
import { Button, Input } from "@amzn/alchemy-components-react";
import { PageProps } from "src/pages/page-interface";
import { AlertBar, AlertBarProps } from "src/components/alert-bar";
import {
    AFTX_MILO_REGISTRATION_ENDPOINT,
    AFTX_MILO_REGISTRATION_FIT_CLIENT_ID,
    AFTX_MILO_REGISTRATION_FIT_TENANT,
    SCC_MILO_REGISTRATION_FIT_ENDPOINT,
    MILO_REGISTRATION_ERROR_STATUS,
    MILO_REGISTRATION_SUCCESS_STATUS,
    OTP_LENGTH,
    PIS_ERROR_OTP_EXPIRED,
    PIS_ERROR_OTP_EXPIRED_TRANSLATION_KEY,
    PIS_ERROR_OTP_NOT_FOUND,
    PIS_ERROR_OTP_NOT_FOUND_TRANSLATION_KEY,
    PIS_ERROR_OTP_USED,
    PIS_ERROR_OTP_USED_TRANSLATION_KEY,
    PIS_ERROR_GENERIC_TRANSLATION_KEY,
    PROXY_IDP_SUCCESS_STATUS,
    SCC_MILO_REGISTRATION_ENDPOINT,
    SCC_MILO_REGISTRATION_FIT_CLIENT_ID,
    SCC_MILO_REGISTRATION_FIT_TENANT,
    AFTX_MILO_REGISTRATION_FIT_ENDPOINT,
} from "src/common/constants";
import { logger } from "src/logger";
import { resolveI18nMessage, useI18nMessage } from "src/common/i18n-message";
import initialMetricsPublisher from 'src/metrics';
import * as KatalMetrics from "@amzn/katal-metrics";

const cloudWatchDimensions = [
    new KatalMetrics.Metric.String('page', 'mfa-registration'),
]
// @ts-ignore
const additionalMetricsContext = new KatalMetrics.Context({cloudWatchDimensions});
const otpRedirectMetricPublisher = initialMetricsPublisher.newChildActionPublisherForMethod('otp-redirect', additionalMetricsContext);
const miloRedirectMetricPublisher = initialMetricsPublisher.newChildActionPublisherForMethod('milo-redirect', additionalMetricsContext);

/**
 * @description MFA Registration Redirection Logic
 *  Source: {@link https://plantuml.corp.amazon.com/plantuml/form/encoded.html#encoded=ZLJBRjim4BppAnQ-soq2z694ILhO1WHKYM4xkGH5mOfhXp15gdpOIOh-UvUKP5XENEGYGAlTcU7iK3VES-j3fHWFtkXG_KBBt5RgcbjUGSMV2tIERt0fNn4kldncZ9VUMBXtrCYyz0gXc7s51MwayvPxQJJCbDapbcNmm9KKt2Fu9uIxrHou5b1QZ2KEitm5IuAF8tlfdrBNV550BY3BMAI0uHNCYljy3CkIQmVVk2sDG205fPBbri6qubA1adeBte138Y2Yg5Bk8asSPl6HeFBLcAGAQR7qiJzANw7csZSAeaRRVlyPhCp-hAiW_rwJJJwup8Mo9C73sfhd5obg5ez0kFDysSVTTGVlQybNZ5fXs8qyzDgKRmhf3I1rjfeFzOEwo1BzEbPxREzQUd8ZcKjOhSx6oiL_81WgXv3htSU59EEkqLfZiz5eTChsWGc1FOi0kdt528nX8bsj-0iC5lWxeFDmY_i4M3NP7C44FQN0ZU5o7ZUJYpaazAi10AD8i1v5J4HCPvDP2baq8IPXWZjP8WGdzIOL6oz5BwcKp4vUaN-_EyaJLHAkqNJiLngTQb4RIVdw55HbZsx9IKJJvENXp0TjZQOKZbPUxwejcqIkWoBR4iR1-DFGLbAPizwsED1UTtYStav-D65uZtP6TgCuIxccpy7Y-vI3ZlFCEWSTEUtjpTfkg1f_L_y0}
 *
 * @param otp 16 Digit One Time Password String
 * @param status Status QueryString Parameter received
 * @param error error Query String Parameter received
 * @param setMessage  Function to set the message in the alertBar
 * @param t Translate Function
 * @returns void
 */
export const mfaRedirect = (
    otp: string | null,
    status: string | null,
    error: string | null,
    setMessage: CallableFunction,
    tenantId: TenantID,
    t: TFunction
) => {
    if (otp) {
        otpValidateRedirect(otp, tenantId);
        return;
    }

    if (error) {
        logger.info("Invalid OTP");
        otpRedirectMetricPublisher.publishCounterMonitor('otp-redirect.ERROR', 1)

        let message;
        switch (error) {
            case PIS_ERROR_OTP_USED:
                message = t(PIS_ERROR_OTP_USED_TRANSLATION_KEY);
                otpRedirectMetricPublisher.publishCounterMonitor('otp-redirect.ERROR.OTP_USED', 1)
                break;
            case PIS_ERROR_OTP_EXPIRED:
                message = t(PIS_ERROR_OTP_EXPIRED_TRANSLATION_KEY);
                otpRedirectMetricPublisher.publishCounterMonitor('otp-redirect.ERROR.OTP_EXPIRED', 1)
                break;
            case PIS_ERROR_OTP_NOT_FOUND:
                message = t(PIS_ERROR_OTP_NOT_FOUND_TRANSLATION_KEY);
                otpRedirectMetricPublisher.publishCounterMonitor('otp-redirect.ERROR.OTP_NOT_FOUND', 1)
                break;
            default:
                message = `${t(PIS_ERROR_GENERIC_TRANSLATION_KEY, {error: error})}`
                otpRedirectMetricPublisher.publishCounterMonitor('otp-redirect.ERROR.GENERIC', 1)
                break;
        }
        setMessage({
            result: AlertType.error,
            header: t('mfa-registration-error-header'),
            message: message
        });
        return;
    }

    switch (status) {
        case PROXY_IDP_SUCCESS_STATUS:
            logger.info("MFA Registration Redirect - Valid OTP");
            otpRedirectMetricPublisher.publishCounterMonitor('otp-redirect.SUCCESS', 1)
            registerMfaRedirect(tenantId);
            break;
        case MILO_REGISTRATION_ERROR_STATUS:
            logger.info("MFA Device Registration Failed");
            miloRedirectMetricPublisher.publishCounterMonitor('milo-registration-redirect.ERROR', 1)
            setMessage({
                result: AlertType.error,
                header: t('mfa-registration-error-header'),
                message: t('mfa-registration-error-message')
            });
            break;
        case MILO_REGISTRATION_SUCCESS_STATUS:
            logger.info("MFA Device Registration Success");
            miloRedirectMetricPublisher.publishCounterMonitor('milo-registration-redirect.SUCCESS', 1)
            setMessage({
                result: AlertType.success,
                header: t('mfa-registration-success-header'),
                message: t('mfa-registration-success-message')
            });
            break;
    }
}


export const otpValidateRedirect = (otp: string, tenantId: TenantID) => {
    if (otp.length != OTP_LENGTH) {
        logger.info(`OTP length ${otp.length} is not required ${OTP_LENGTH}`);
        return
    }
    if (!otp.match(/^[0-9]*$/)) {
        logger.info(`OTP ${otp} is not numeric`);
        return
    }
    otpRedirectMetricPublisher.publishCounterMonitor('otp-redirect', 1);

    let redirect_uri;
    let referrer;

    switch (tenantId) {
        case TenantID.AFTX:
            logger.info(`AFTX Tenant: Redirecting to FIT with OTP: ${otp}`);
            otpRedirectMetricPublisher.publishCounterMonitor('otp-redirect-aftx', 1);

            redirect_uri = `${window.location.protocol}//${window.location.host}/${TenantID.AFTX}/mfa-registration/?status=${PROXY_IDP_SUCCESS_STATUS}`;  // does not include query string
            referrer = `${window.location.protocol}//${window.location.host}/${TenantID.AFTX}/mfa-registration/`;

            // replace is used, so the back button works as expected
            window.location.replace(`${AFTX_MILO_REGISTRATION_FIT_ENDPOINT}/${AFTX_MILO_REGISTRATION_FIT_TENANT}/authorize?` +
                `client_id=${AFTX_MILO_REGISTRATION_FIT_CLIENT_ID}` +
                `&redirect_uri=${redirect_uri}` +
                `&response_type=code` +
                `&scope=openid tenantId:${AFTX_MILO_REGISTRATION_FIT_TENANT}` +
                `&referrer=${referrer}` +
                `&otp=${otp}`);

            break;

        case TenantID.SCC:
            logger.info(`SCC Tenant: Redirecting to FIT with OTP: ${otp}`);
            otpRedirectMetricPublisher.publishCounterMonitor('otp-redirect-scc', 1);

            redirect_uri = `${window.location.protocol}//${window.location.host}/${TenantID.SCC}/mfa-registration/?status=${PROXY_IDP_SUCCESS_STATUS}`;  // does not include query string
            referrer = `${window.location.protocol}//${window.location.host}/${TenantID.SCC}/mfa-registration/`;

            // replace is used, so the back button works as expected
            window.location.replace(`${SCC_MILO_REGISTRATION_FIT_ENDPOINT}/${SCC_MILO_REGISTRATION_FIT_TENANT}/authorize?` +
                `client_id=${SCC_MILO_REGISTRATION_FIT_CLIENT_ID}` +
                `&redirect_uri=${redirect_uri}` +
                `&response_type=code` +
                `&scope=openid tenantId:${SCC_MILO_REGISTRATION_FIT_TENANT}` +
                `&referrer=${referrer}` +
                `&otp=${otp}`);
            break;

        default:
            logger.warn("Unknown tenant not redirect to FIT to validate the OTP. Tenant ID: " + tenantId);
            miloRedirectMetricPublisher.publishCounterMonitor('otp-redirect-failure-unknown-tenant', 1);
            break;
    }


}

export const registerMfaRedirect = (tenantId: TenantID) => {
    logger.info("Redirecting to MILO Registration Endpoint")
    miloRedirectMetricPublisher.publishCounterMonitor('milo-redirect', 1);

    switch (tenantId) {
        case TenantID.AFTX:
            miloRedirectMetricPublisher.publishCounterMonitor('milo-redirect-aftx', 1) ;
            logger.info("Redirecting to the AFTX MILO Registration Endpoint: " + AFTX_MILO_REGISTRATION_ENDPOINT)
            // replace is used, so the back button works as expected
            window.location.replace(AFTX_MILO_REGISTRATION_ENDPOINT);
            break;
        case TenantID.SCC:
            miloRedirectMetricPublisher.publishCounterMonitor('milo-redirect-scc', 1) ;
            logger.info("Redirecting to the SCC MILO Registration Endpoint: " + SCC_MILO_REGISTRATION_ENDPOINT)
            // replace is used, so the back button works as expected
            window.location.replace(SCC_MILO_REGISTRATION_ENDPOINT);
            break;
        default:
            logger.warn("Unknown tenant not redirect to a milo registration endpoint. Tenant ID: " + tenantId);
            miloRedirectMetricPublisher.publishCounterMonitor('milo-redirect-failure-unknown-tenant', 1);
            break;
    }
}

/**
 * Input parameters required to create Page.
 */
export interface MFARegistrationPageProps extends PageProps {
    tenantId: TenantID,
}


/**
 * Renders Associate MFA Registration Page.
 * @param props {PageProps}
 */
export const MfaRegistration = (props: MFARegistrationPageProps) => {
    const {t} = useTranslation();
    const searchParams = new URLSearchParams(useLocation().search);
    const [otp, setOtp] = useState<string>(searchParams.get('otp') || '');
    const { message: otpStatus, setKey: setOtpStatus, clear: clearOtpStatus } = useI18nMessage();
    const [statusParam] = useState<string>(searchParams.get('status') || '');
    const [errorParam] = useState<string>(searchParams.get('error') || '');
    const [alertBarProps, setAlertBarProps] = useState<AlertBarProps|null>(null);

    useEffect(() => {
        logger.info(`Query string parameters sent in url request : ${searchParams.toString()}`);

        // Leave OTP param null here to avoid automatic redirection to FIT /authorize on page load since some email
        // providers (such as Outlook) auto-invoke URLs in emails, so wait till user explicitly clicks Submit button
        mfaRedirect(null, statusParam, errorParam, setAlertBarProps, props.tenantId, t);  // Update status/error based on searchParams

        props.setActivePage(PageType.MFA_REGISTRATION);
        document.getElementById('otp-input')?.focus();
    }, []);

    /**
     * Validates OTP.
     *
     * @param value {string} otp
     * @param submit {boolean} intent is to submit
     * @returns {boolean} of the valid status
     */
    const validOtp = (value: string, submit: boolean): boolean => {
        logger.info(`Validating OTP: "${value}"`);

        let valid = true;
        let translateString = "otp";
        if (submit && value.length !== OTP_LENGTH) {
            // Only validate the length when submitting
            logger.info(`OTP '${value}' is not required ${OTP_LENGTH} characters. It is ${value.length}.`);
            valid = false;
            translateString += "-length";
        }
        if (!value.match(/^[0-9]*$/)) {
            logger.info(`OTP ${value} is not only numeric.`);
            valid = false;
            translateString += "-numeric";
        }

        if (!valid) {
            setOtpStatus(translateString + "-invalid");

        } else {
            clearOtpStatus();
        }
        return valid;
    }

    const handleBarCodeInput = (e: any) => {
        const value = e.target.value;
        setOtp(value);
    }

    const handlePressEnter = (e: any) => {
        if(e.key === 'Enter') {
            handleSubmit();
        }
    }

    const handleSubmit = () => {
        logger.info("Attempting to submit the OTP for MFA Registration.")
        if (validOtp(otp, true)) {
            mfaRedirect(otp, null, null, setAlertBarProps, props.tenantId,  t);
        }
    }

    return (
        <div className="container-fluid">
            <div className="row">
                <BreadCrumbs breadcrumbItems={[{tag: t('register-mfa')}]}/>
            </div>

            <div className="row b-background mx-0 mb-0-5">
                <div className="col align-self-center title m-0 py-1">{t('mfa-registration-header')}</div>
            </div>

            <div className="row d-flex py-2 mx-0 justify-content-center b-background">
                <div className="col-12 align-self-center m-0 py-1">
                    <Input
                        id="otp-input"
                        maxlength={16}
                        label={t('otp-label')}
                        helpText={t('otp-help-text')}
                        placeholder={t('scan-barcode')}
                        value={otp}
                        onInput={handleBarCodeInput}
                        status={resolveI18nMessage(t, otpStatus) ?? ''}
                        onKeyDown={handlePressEnter}
                    />
                </div>
            </div>

            <div className="mt-0 py-2 mx-0 mb-5 row d-flex justify-content-center b-background">
                <Button
                    id="otp-button"
                    size="lg"
                    label={t('submit')}
                    onClick={handleSubmit}
                />
            </div>

            {alertBarProps != null &&
                <AlertBar
                    result={alertBarProps.result}
                    header={alertBarProps.header}
                    message={alertBarProps.message}
                />
            }
        </div>
    )
};